import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { canConfirmDelete } from '../../utils/deleteUser';

// Deleting a user is deliberately slow (Lucas, 2026-09-24): type their email
// and business name, re-enter the admin password, wait five seconds. The
// account is then soft-deleted — signed out, restorable for 7 days.
const ARM_SECONDS = 5;

interface Preview {
  email: string;
  business_name: string;
  counts: Record<string, number>;
  blocked_reason: string | null;
}

const LABELS: Record<string, string> = {
  transactions: 'sales rows', payments: 'payments', bills: 'bills',
  uploads: 'uploads', briefs: 'briefs', pos_connections: 'register connections',
};

export default function DeleteUserDialog({ userId, onClose, onDeleted }: {
  userId: string;
  onClose: () => void;
  onDeleted: (purgeAfter: string) => void;
}) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [password, setPassword] = useState('');
  const [countdown, setCountdown] = useState(ARM_SECONDS);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`/api/admin/users/${userId}/deletion-preview`)
      .then((res) => setPreview(res.data))
      .catch(() => { toast.error("Couldn't load this account"); onClose(); });
  }, [userId]); // eslint-disable-line

  const matches = !!preview && !preview.blocked_reason
    && canConfirmDelete({ email, businessName, password }, preview);

  useEffect(() => {
    if (!matches) { setCountdown(ARM_SECONDS); return; }
    if (countdown === 0) return;
    const t = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [matches, countdown]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`/api/admin/users/${userId}/delete`, {
        confirm_email: email, confirm_business_name: businessName, admin_password: password,
      });
      toast.success(`Deleted. Restorable until ${new Date(res.data.purge_after).toLocaleDateString()}.`);
      onDeleted(res.data.purge_after);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Couldn't delete this account");
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  const input = 'w-full border border-[#E6E2DE] focus:ring-2 focus:ring-[#B54A4A]/20 focus:border-[#B54A4A] rounded-[8px] h-10 px-3 font-sans text-sm outline-none';

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[18px] shadow-xl border border-[#E6E2DE] w-full max-w-md" role="dialog" aria-labelledby="delete-user-title">
        <div className="p-6 border-b border-[#E6E2DE]">
          <h3 id="delete-user-title" className="font-serif text-lg text-[#2D2A2E]">Delete this account?</h3>
          {preview && (
            <p className="font-sans text-sm text-[#6B6670] mt-1">
              {preview.email} · {Object.entries(preview.counts).filter(([, n]) => n > 0)
                .map(([k, n]) => `${n} ${LABELS[k] || k}`).join(' · ') || 'no data'}
            </p>
          )}
        </div>
        <div className="p-6 space-y-3">
          {!preview ? (
            <p className="font-sans text-sm text-[#6B6670]">Loading…</p>
          ) : preview.blocked_reason ? (
            <p className="font-sans text-sm text-[#B54A4A]">{preview.blocked_reason}</p>
          ) : (
            <>
              <label className="block font-sans text-xs text-[#6B6670]">
                Type their email
                <input className={`${input} mt-1`} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
              </label>
              <label className="block font-sans text-xs text-[#6B6670]">
                Type their business name ({preview.business_name})
                <input className={`${input} mt-1`} value={businessName} onChange={(e) => setBusinessName(e.target.value)} autoComplete="off" />
              </label>
              <label className="block font-sans text-xs text-[#6B6670]">
                Your admin password
                <input className={`${input} mt-1`} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
              </label>
              <p className="font-sans text-xs text-[#6B6670]">Signed out now. Restorable for 7 days, then removed for good.</p>
            </>
          )}
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 bg-white border border-[#E6E2DE] text-[#2D2A2E] hover:bg-[#FAF9F7] rounded-[8px] h-10 font-sans font-medium text-sm transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!matches || countdown > 0 || busy}
            className="flex-1 bg-[#B54A4A] text-white hover:bg-[#9E3E3E] rounded-[8px] h-10 font-sans font-medium text-sm transition-colors disabled:opacity-40"
          >
            {busy ? 'Deleting…' : matches && countdown > 0 ? `Delete in ${countdown}` : 'Delete account'}
          </button>
        </div>
      </div>
    </div>
  );
}
